/**
 * Firm Pulse - Mobile KPI Dashboard
 * Traffic light overview of all clients with key metrics.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader2, TrendingUp, TrendingDown, Minus, Users, AlertTriangle, CheckCircle, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';
import { ThemeToggle } from './theme';
import ManagerSecuritySettings from './ManagerSecuritySettings';

export default function PulsePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning' | 'healthy'>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['manager-pulse'],
    queryFn: async () => { const r = await apiClient.get('/firm/dashboard'); return r.data.data; },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--v4-accent)]" />
      </div>
    );
  }

  const rawClients = data?.clients || [];
  const clients = rawClients.map((c: any) => ({
    id: c.id,
    name: c.display_name,
    score: c.health_score ?? 50,
    xeroConnected: !!c.xero_org_id,
    industry: c.industry,
    status: c.status,
  }));

  // Traffic light categorization
  const getStatus = (score: number) => {
    if (score >= 70) return 'healthy';
    if (score >= 40) return 'warning';
    return 'critical';
  };

  const filteredClients = clients
    .filter((c: any) => {
      if (search && !c.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (filter !== 'all' && getStatus(c.score || 50) !== filter) return false;
      return true;
    })
    .sort((a: any, b: any) => (a.score || 50) - (b.score || 50));

  const critical = clients.filter((c: any) => getStatus(c.score || 50) === 'critical').length;
  const warning = clients.filter((c: any) => getStatus(c.score || 50) === 'warning').length;
  const healthy = clients.filter((c: any) => getStatus(c.score || 50) === 'healthy').length;

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-4">
      <h2 className="text-lg font-medium">Firm Pulse</h2>

      {/* Traffic Light Summary */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => setFilter(filter === 'critical' ? 'all' : 'critical')}
          className={cn('p-3 rounded-xl border text-center transition',
            filter === 'critical' ? 'bg-red-500/10 border-red-500/30' : 'bg-muted/50 border-border'
          )}
        >
          <p className="text-xl font-bold text-red-600 dark:text-red-400">{critical}</p>
          <p className="text-xs text-muted-foreground uppercase">Critical</p>
        </button>
        <button
          onClick={() => setFilter(filter === 'warning' ? 'all' : 'warning')}
          className={cn('p-3 rounded-xl border text-center transition',
            filter === 'warning' ? 'bg-amber-500/10 border-amber-500/30' : 'bg-muted/50 border-border'
          )}
        >
          <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{warning}</p>
          <p className="text-xs text-muted-foreground uppercase">Attention</p>
        </button>
        <button
          onClick={() => setFilter(filter === 'healthy' ? 'all' : 'healthy')}
          className={cn('p-3 rounded-xl border text-center transition',
            filter === 'healthy' ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/50 border-border'
          )}
        >
          <p className="text-xl font-bold text-green-600 dark:text-green-400">{healthy}</p>
          <p className="text-xs text-muted-foreground uppercase">Healthy</p>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-muted/40 border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-[var(--v4-accent)]/30"
        />
      </div>

      {/* Client List */}
      <div className="space-y-2">
        {filteredClients.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            {search ? 'No clients match your search' : 'No client data available'}
          </div>
        ) : (
          filteredClients.map((client: any) => {
            const score = client.score || 50;
            const status = getStatus(score);

            return (
              <div
                key={client.id || client.name}
                onClick={() => client.id && navigate(`/pa/client/${client.id}`)}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer active:scale-[0.98]',
                  status === 'critical' ? 'border-red-500/20 bg-red-500/5' :
                  status === 'warning' ? 'border-amber-500/10 bg-amber-500/5' :
                  'border-border bg-muted/50'
                )}
              >
                {/* Traffic Light Dot */}
                <div className={cn(
                  'w-3 h-3 rounded-full shrink-0',
                  status === 'critical' ? 'bg-red-500' :
                  status === 'warning' ? 'bg-amber-500' :
                  'bg-green-500'
                )} />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{client.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {client.xeroConnected && (
                      <span className="text-xs text-green-600 dark:text-green-500">Xero</span>
                    )}
                    {client.industry && (
                      <span className="text-xs text-muted-foreground truncate">{client.industry}</span>
                    )}
                  </div>
                </div>

                {/* Score */}
                <div className="text-right shrink-0">
                  <p className={cn(
                    'text-lg font-bold',
                    status === 'critical' ? 'text-red-600 dark:text-red-400' :
                    status === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                    'text-green-600 dark:text-green-400'
                  )}>{score}</p>
                  <p className="text-xs text-muted-foreground">health</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Appearance */}
      <div className="bg-card border border-border rounded-xl p-4">
        <h3 className="text-sm font-semibold text-foreground">Appearance</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">Choose how the app looks</p>
        <div className="mt-3">
          <ThemeToggle />
        </div>
      </div>

      {/* Security: PIN + sign out */}
      <ManagerSecuritySettings />
    </div>
  );
}
